// Editorul de reguli de fațadă, lângă machetă.
//
// Regulile sunt o listă ordonată — ULTIMA CÂȘTIGĂ — și fiecare rând e o
// propoziție: «pe fețele X, la etajele Y, [pe porțiunea Z]: finisaj | brâu |
// balustradă». Panoul le arată exact așa, ca omul să le citească de sus în jos
// cum le aplică motorul, și le lasă și ca JSON, pentru cine vrea tot.
//
// REGULILE SUNT ALE SOLUȚIEI DESCHISE (Cătălin, 6.10.2026), nu ale proiectului:
// panoul le citește din documentul arătat și le scrie înapoi pe același rând
// (v. `stilSolutie.ts`). Nici variantele surori, nici stilul proiectului nu se
// ating de aici.
//
// TREI GESTURI, trei costuri: «Previzualizează» reface doar etajele și pune
// fațada în machetă (secunde, nimic scris); «Salvează» scrie stilul pe soluție
// (secunde, cu anulare cât stai pe proiect); «Regenerează» pornește o
// arhitectură nouă din planul de situație (minute). Asistentul de jos lucrează
// prin aceleași scrieri.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRegistruOptional } from "../../nav/Registru";
import { numeRand } from "../../nav/selectie";
import { PASI_CARE_SCRIU, useFirAgent } from "./firAgent";
import { eticheta, fisaElement, fisaZona, type Fisa } from "./selectie";
import type { ZonaMacheta } from "../plan/selectie3d";
import * as api from "../../api";
import type { ReguleFatada, StilFatada } from "../../api";
import Asistent from "../../asistent/Asistent";
import Inspiratie, { carteaDin } from "./Inspiratie";
import { PASII_FATADEI, legatDeSolutie, unelteFatada, type ContextFatada } from "../../asistent/unelteFatada";
import { bcp47, esteCheie, limba, useT } from "../../i18n";
import { useRolProiect } from "../../components/rol";
import type { CanonicalDoc } from "../canonical";
import { aceeasiSectiune, balustradele, cateRefaceri, cativaPasi, golesteStiva, punePasInapoi, scoatePas,
         scoateRefacere, stareaFatadei, tinePas, tineRefacere, ultimaRefacere, ultimulPas } from "./stilSolutie";

/** MACHETA, văzută de panou: documentul de pe ecran luat ca instantaneu și pus
 *  la loc pe loc — anularea și refacerea nu mai așteaptă scrierea și recitirea
 *  celor ~20 MB, nu clipesc și nu mută camera. Instantaneul e opac: îl dă și îl
 *  ia vizualizatorul, în forma stării lui. */
export type MachetaFatadei = {
  /** Documentul de pe ecran, întreg (`null` cât se mai aduce o felie). */
  instantaneu: () => unknown;
  /** Pune pe ecran un instantaneu; recitirea care urmează scrierii îl adoptă. */
  restabileste: (instantaneu: unknown) => void;
  /** Urmează o scriere a panoului: schimbarea de `updated_at` pe care o aduce nu
   *  redeschide documentul de la zero (camera, pânza). */
  urmeazaScriere: () => void;
};

type Props = {
  projectId: string;
  /** Soluția de ARHITECTURĂ pe care se previzualizează (cea legată de planul
   *  de situație arătat, sau chiar cea deschisă). */
  arhitecturaId: string | null;
  /** Planul de situație din care s-ar regenera arhitectura; `null` când nu e cazul. */
  planSituatieId: string | null;
  /** Documentul de arhitectură arătat acum — din el se citesc fețele pentru
   *  asistent și în el se pun obiectele previzualizate. */
  doc: CanonicalDoc | null;
  /** Primește obiectele de fațadă previzualizate, ca să le pună peste document. */
  laPreview: (obiecte: unknown[]) => void;
  /** După pornirea unei regenerări, ca pagina să arate jobul. */
  laJob?: () => void;
  /** FAȚADA S-A SCHIMBAT PE SOLUȚIE (o scriere din panou sau din discuție, o
   *  scriere a agentului, sfârșitul rulării lui): macheta își recitește
   *  documentul, cu camera unde era. */
  laSchimbare?: (adoptat?: boolean) => void;
  /** Instantaneele machetei pentru anulare și refacere (v. `MachetaFatadei`). */
  macheta?: MachetaFatadei;
  /** SELECȚIA DE PE MACHETĂ (elementele alese cu clic, zonele trase cu ⌥/Alt),
   *  ținută de vizualizator; panoul o face fișe pentru asistent și agent. */
  selectie?: { elemente: string[]; zone: ZonaMacheta[] };
  laSelectie?: (s: { elemente: string[]; zone: ZonaMacheta[] }) => void;
  /** Cipul de sub mouse (`e:<id>`, `z:<indice>`; `null` = niciunul): macheta îl
   *  aprinde mai tare. */
  laAprinde?: (cheie: string | null) => void;
  /** Locurile asistentului în vizualizator (v. `Asistent::loc`): panoul în
   *  coloana din dreapta, sub panoul ăsta; butonul lângă 2D/3D. */
  locAsistent?: { panou: HTMLElement | null; buton: HTMLElement | null };
};

const SUPRAFETE = ["facade", "plinth", "recess", "parapet", "soffit", "balcony",
                   "terrace", "loggia"] as const;
/** PARDOSELILE (format 3.8): deck-ul numai pe terase */
const PARDOSELI = ["tiles", "raised", "deck"] as const;
const MATERIALE_PARDOSELII: Record<string, string[]> = {
  tiles: ["gresie_exterior"],
  raised: ["dale_portelanate", "dale_terasa"],
  deck: ["deck_compozit", "deck_lemn"],
};
const CULOARE_PARDOSELII: Record<string, string> = { tiles: "#a19c95", raised: "#b0aba3", deck: "#3a3c3f" };
const ETAJE = ["all", "ground", "upper", "top"] as const;
const FINISAJE = ["plaster", "cladding", "brick", "glazing"] as const;
const SECTIUNI = ["rect", "cornice", "slope", "half_round"] as const;
const BALUSTRADE = ["glass", "slats", "solid"] as const;
const MATERIALE: Record<string, string[]> = {
  plaster: ["tencuiala_decorativa"],
  cladding: ["placaj_piatra", "piatra_travertin", "piatra_granit", "piatra_ardezie", "piatra_zidita",
             "placaj_hpl", "lemn_ingust", "lemn_vertical", "lemn_riflaj", "lemn_panouri",
             "lamele_verticale", "panou_metal"],
  brick: ["caramida_aparenta"],
};

/** Greșelile unui refuz: lista `erori` a validatorului de stil, când e a lui,
 *  altfel mesajul. */
function eroriDin(err: unknown): string[] {
  const e = err as { date?: { erori?: unknown }; message?: string };
  const lista = e?.date?.erori;
  if (Array.isArray(lista) && lista.length) return lista.map(String);
  return [e?.message ?? String(err)];
}

type FelRegula = "finish" | "band" | "railing" | "window" | "floor" | "tile";
const felRegulii = (r: ReguleFatada): FelRegula =>
  r.band ? "band" : r.railing ? "railing" : r.window ? "window" : r.floor ? "floor" : r.tile ? "tile" : "finish";

const VITRAJE = ["single", "double", "triple"] as const;
const DESCHIDERI = ["fixed", "casement", "tilt_turn", "sliding"] as const;

function regulaNoua(fel: FelRegula): ReguleFatada {
  if (fel === "window") {
    return { select: { surface: "facade", openings: "all" },
             window: { frame: { material: "pvc", profile_m: 0.07 }, glazing: "double",
                       leaf_max_m: 0.9, opening: "tilt_turn", colour: "#f4f4f2",
                       sill: { material: "glaf_aluminiu", depth_m: 0.04, height_m: 0.03, overhang_m: 0.03 },
                       reveal: { material: "etics", depth_m: 0.03 } } };
  }
  if (fel === "band") {
    return { select: { surface: "facade", storeys: "upper" },
             band: { at: "slab", height_m: 0.3, depth_m: 0.05, section: "rect",
                     material: "profil_eps", colour: "#f2efe6" } };
  }
  if (fel === "railing") {
    return { select: { surface: "balcony", storeys: "all" },
             railing: { kind: "glass", height_m: 1.1, material: "parapet_sticla" } };
  }
  if (fel === "floor") {
    return { select: { surface: "balcony", storeys: "all" },
             floor: { kind: "tiles", material: "gresie_exterior", colour: "#a19c95" } };
  }
  return { select: { surface: "facade", storeys: "all" },
           finish: { kind: "plaster", material: "tencuiala_decorativa", colour: "#efe9dd" } };
}

/** Etajele, ca text scurt pentru selectorul de etaje. */
function etajeleCa(st: NonNullable<ReguleFatada["select"]>["storeys"] | undefined): string {
  if (st == null || st === "all") return "all";
  if (typeof st === "string") return st;
  if (Array.isArray(st)) return st.join(",");
  return `${st.from ?? 0}-${st.to ?? ""}`;
}

function etajeleDin(text: string): NonNullable<NonNullable<ReguleFatada["select"]>["storeys"]> {
  const t = text.trim();
  if ((ETAJE as readonly string[]).includes(t)) return t as "all";
  const m = t.match(/^(\d+)\s*-\s*(\d*)$/);
  if (m) return { from: Number(m[1]), ...(m[2] ? { to: Number(m[2]) } : {}) };
  const nums = t.split(",").map((x) => Number(x.trim())).filter((x) => Number.isInteger(x));
  return nums.length ? nums : "all";
}

/** Fețele fiecărui corp, pentru asistent: din obiectele `Facade` ale documentului. */
function feteleDin(doc: CanonicalDoc | null) {
  if (!doc) return [];
  const corpuri = new Map<string, string>();
  for (const o of doc.objects ?? []) {
    if (o.type === "Building") corpuri.set(o.id, String(o.properties.code ?? o.id));
  }
  return (doc.objects ?? [])
    .filter((o) => o.type === "Facade" && (o.properties as { surface?: string }).surface === "facade")
    .map((o) => {
      const pr = o.props as Record<string, { bays?: unknown; solid?: unknown }> | undefined;
      return {
        corp: corpuri.get(String(o.properties.building ?? "")) ?? "?",
        fata: String(o.properties.name ?? o.id).replace(/^Fața /, ""),
        run: (o.properties as { run?: number }).run,
        side: (o.properties as { side?: number }).side,
        lungime_m: o.geometry?.length_m,
        bays: pr?.["architecture.facade"]?.bays,
      };
    });
}

/** Rezumatul unei reguli, pe un rând: unde · ce. */
function rezumat(r: ReguleFatada, tk: (k: string) => string): { unde: string; ce: string; culoare?: string } {
  const sel = r.select ?? {};
  const unde = [tk(`fatada.surf.${sel.surface ?? "facade"}`), etajeleCa(sel.storeys),
                sel.region?.u ? `${sel.region.u[0]}→${sel.region.u[1]}` : ""].filter(Boolean).join(" · ");
  if (r.band) {
    return { unde, culoare: r.band.colour,
             ce: `${tk("fatada.brau")} ${r.band.z ? `${r.band.z[0]} → ${r.band.z[1]}` : `${tk(`fatada.ancora.${String(r.band.at ?? "slab")}`)} · ${r.band.height_m}`}×${r.band.depth_m ?? 0.03} m${typeof r.band.section === "string" && r.band.section !== "rect" ? ` · ${tk(`fatada.sectiune.${r.band.section}`)}` : ""}` };
  }
  if (r.railing) {
    return { unde, culoare: r.railing.colour,
             ce: `${tk(`kind.${r.railing.kind}`)} · h ${r.railing.height_m ?? 1.1}` };
  }
  if (r.floor) {
    const fm = r.floor.format_m;
    return { unde: `${tk("fatada.pardoseala")} · ${unde}`, culoare: r.floor.colour,
             ce: `${tk(`fatada.pardoseala.${r.floor.kind}`)}${fm ? ` · ${fm[0]}×${fm[1]} m` : ""}${r.floor.material ? ` · ${r.floor.material}` : ""}` };
  }
  if (r.window) {
    const w = r.window;
    const canate = w.leaves ? `${w.leaves} ${tk("fatada.canate")}` : `≤ ${w.leaf_max_m ?? 0.9} m/${tk("fatada.canat")}`;
    return { unde: `${tk("fatada.tamplarie")} · ${unde}`, culoare: w.colour,
             ce: `${w.frame?.material ?? "pvc"} · ${tk(`fatada.vitraj.${w.glazing ?? "double"}`)} · ${canate}`
                 + (w.surround ? ` · ${tk("fatada.rama")} ${w.surround.width_m ?? 0.2} m` : w.sill === null ? ` · ${tk("fatada.fara_glaf")}` : "") };
  }
  if (r.tile) {
    const z = sel.region?.z;
    return { unde, culoare: r.tile.colour,
             ce: `${tk("fatada.placi_colorate")}${z ? ` · ${z[0]} → ${z[1]}` : ""}` };
  }
  return { unde, culoare: r.finish?.colour,
           ce: `${tk(`kind.${r.finish?.kind ?? "plaster"}`)} · ${r.finish?.material ?? ""}` };
}

/** Câte dimensiuni debitate se arată sub un rând de plăci înainte de «încă…»:
 *  pe un corp real sunt zeci (50 la ceramică, 123 la piatră pe Demo, C6). */
const DIM_VIZIBILE = 6;
const cheiePlaci = (r: api.RandPlaci) => `${r.material}|${r.colour ?? ""}|${r.module_m.join("x")}`;

/** Lista de plăci a TUTUROR corpurilor, adunată pe material, culoare și format. */
export function adunaPlacile(peCorp: Record<string, api.RandPlaci[]>): api.RandPlaci[] {
  const m = new Map<string, api.RandPlaci & { dim: Map<string, { width_m: number; height_m: number; count: number }> }>();
  for (const l of Object.values(peCorp)) {
    for (const r of l) {
      const k = `${r.material}|${r.colour ?? ""}|${r.module_m[0]}|${r.module_m[1]}`;
      const g = m.get(k) ?? { ...r, count: 0, area_m2: 0, sizes: [], dim: new Map() };
      g.count += r.count;
      g.area_m2 += r.area_m2;
      for (const d of r.sizes) {
        const kd = `${d.width_m}x${d.height_m}`;
        const v = g.dim.get(kd) ?? { width_m: d.width_m, height_m: d.height_m, count: 0 };
        v.count += d.count;
        g.dim.set(kd, v);
      }
      m.set(k, g);
    }
  }
  return [...m.values()]
    .map(({ dim, ...r }) => ({ ...r, area_m2: Math.round(r.area_m2 * 1000) / 1000,
                               sizes: [...dim.values()].sort((a, b) => b.count - a.count) }))
    .sort((a, b) => a.material.localeCompare(b.material) || b.count - a.count);
}

export default function PanouFatada({ projectId, arhitecturaId, planSituatieId, doc,
                                      laPreview, laJob, laSchimbare, macheta, selectie, laSelectie,
                                      laAprinde, locAsistent }: Props) {
  const t = useT();
  /** Cheile compuse la rulare (`fatada.surf.${s}`) nu sunt literale pentru
   *  verificatorul de tipuri; toate există în catalog (v. `i18n.test.ts`). */
  const tk = (k: string) => t(k as never);
  const nf0 = useMemo(() => new Intl.NumberFormat(bcp47(limba()), { maximumFractionDigits: 0 }), []);
  const nf1 = useMemo(() => new Intl.NumberFormat(bcp47(limba()), { minimumFractionDigits: 1, maximumFractionDigits: 1 }), []);
  const nf2 = useMemo(() => new Intl.NumberFormat(bcp47(limba()), { minimumFractionDigits: 2, maximumFractionDigits: 2 }), []);
  const numeMaterial = (m: string) => (esteCheie(`material.${m}`) ? tk(`material.${m}`) : m.replace(/_/g, " "));
  const { potScrie } = useRolProiect();
  const [stil, setStil] = useState<StilFatada | null>(null);
  const [salvat, setSalvat] = useState<StilFatada | null>(null);
  const [presets, setPresets] = useState<Record<string, StilFatada>>({});
  const [json, setJson] = useState(false);
  const [text, setText] = useState("");
  const [erori, setErori] = useState<string[]>([]);
  const [lucrez, setLucrez] = useState<"" | "preview" | "save" | "job" | "anulez" | "refac">("");
  const registrul = useRegistruOptional();
  const [mesaj, setMesaj] = useState("");
  const [cantitati, setCantitati] = useState<Record<string, Record<string, number>> | null>(null);
  /** LISTA DE PLĂCI (fațada ventilată): din previzualizare, altfel din corpurile
   *  documentului deschis. */
  const [placiPreview, setPlaciPreview] = useState<Record<string, api.RandPlaci[]> | null>(null);
  const placiDoc = useMemo(() => {
    const out: Record<string, api.RandPlaci[]> = {};
    for (const o of doc?.objects ?? []) {
      if (o.type !== "Building") continue;
      const l = ((o.props as Record<string, { placi?: api.RandPlaci[] }> | undefined)?.["architecture.fatada"]?.placi) ?? [];
      if (l.length) out[String(o.properties.code ?? o.id)] = l;
    }
    return out;
  }, [doc]);
  const placi = useMemo(() => adunaPlacile(placiPreview ?? placiDoc), [placiPreview, placiDoc]);
  /** Rândurile cu toate dimensiunile desfăcute (altfel, primele `DIM_VIZIBILE`). */
  const [placiDeschise, setPlaciDeschise] = useState<Set<string>>(() => new Set());
  const [strans, setStrans] = useState(false);
  /** Regula desfăcută pentru editare; restul stau pe un rând. */
  const [desfacuta, setDesfacuta] = useState<number | null>(null);
  const [, setAsistentDeschis] = useState(false);
  // AGENTUL DE FAȚADĂ pe arhitectura asta: rulează acum? (din registrul viu,
  // ca la vizualizator) — și firul lui, pentru butonul asistentului
  const agentRuleaza = !!arhitecturaId && registrul?.registru?.solutii.some(
    (s) => s.id === arhitecturaId && s.rulare?.kind === "facade") === true;
  const fir = useFirAgent(projectId, arhitecturaId ?? null, agentRuleaza);
  // INSPIRAȚIA: referințele cu care agentul a generat fațada soluției (cartea ei)
  const randArh = registrul?.registru?.solutii.find((s) => s.id === arhitecturaId);
  const carte = carteaDin(randArh?.summary);
  // sub titlul asistentului: soluția pe care o schimbă, cu numele din antet
  const solutiaNumita = randArh ? numeRand(randArh) : undefined;

  // STAREA DE PE SOLUȚIE: stilul, revizia, compoziția. Panoul se reface din ea
  // numai când se schimbă soluția sau revizia — nu la fiecare document nou
  // (o previzualizare pune alte fețe peste el, iar ciorna omului ar pieri).
  const stare = useMemo(() => stareaFatadei(doc), [doc]);
  const stareRef = useRef(stare);
  stareRef.current = stare;
  const areDoc = !!doc;
  const [pasi, setPasi] = useState(0);
  const [refaceri, setRefaceri] = useState(0);
  const numara = useCallback(() => {
    if (!arhitecturaId) return;
    setPasi(cativaPasi(projectId, arhitecturaId));
    setRefaceri(cateRefaceri(projectId, arhitecturaId));
  }, [projectId, arhitecturaId]);
  /** REVIZIA SCRISĂ de panou pe soluția asta. Un instantaneu pus la loc poartă
   *  revizia de când a fost luat, iar soluția e deja mai departe: garda scrierii
   *  ia cea mai mare dintre cele două (reviziile doar cresc). */
  const revizieScrisa = useRef<{ sol: string | null; r: number }>({ sol: null, r: 0 });
  const revizia = useCallback(() => {
    const scrisa = revizieScrisa.current.sol === arhitecturaId ? revizieScrisa.current.r : 0;
    return Math.max(stareRef.current.revizie, scrisa);
  }, [arhitecturaId]);
  const tineRevizia = (r: number) => {
    const v = revizieScrisa.current;
    revizieScrisa.current = { sol: arhitecturaId, r: v.sol === arhitecturaId ? Math.max(v.r, r) : r };
  };
  useEffect(() => {
    if (!areDoc) return;
    const s = stareRef.current;
    setStil(s.stil); setSalvat(s.stil);
    numara();
  }, [projectId, arhitecturaId, stare.revizie, areDoc, numara]);

  useEffect(() => {
    let viu = true;
    api.getFatadaPresets().then((p) => { if (viu) setPresets(p); }).catch(() => undefined);
    return () => { viu = false; };
  }, []);

  useEffect(() => { if (json) setText(JSON.stringify(stil ?? presets.implicit ?? { rules: [] }, null, 2)); },
            [json, stil, presets]);

  const reguli = useMemo(() => stil?.rules ?? presets.implicit?.rules ?? [], [stil, presets]);
  const schimbat = JSON.stringify(stil) !== JSON.stringify(salvat);

  const pune = useCallback((r: ReguleFatada[]) => {
    setStil({ version: 1, ...(stil ?? {}), rules: r });
  }, [stil]);

  const previzualizeaza = useCallback(async (s: StilFatada | null) => {
    if (!arhitecturaId) { setMesaj(t("fatada.fara_arh")); return { erori: [] as string[] }; }
    setLucrez("preview"); setErori([]); setMesaj("");
    try {
      const r = await api.previewFatada(projectId, arhitecturaId, s);
      laPreview(r.objects);
      setCantitati(r.cantitati);
      setPlaciPreview(r.placi ?? null);
      setMesaj(t("fatada.previzualizat", { s: r.durata_s.toFixed(0) }));
      return { erori: [] as string[], cantitati: r.cantitati, durata_s: r.durata_s };
    } catch (err) {
      const lista = eroriDin(err);
      setErori(lista);
      return { erori: lista };
    } finally {
      setLucrez("");
    }
  }, [projectId, arhitecturaId, laPreview, t]);

  /** Macheta ia documentul nou: registrul recitit schimbă `updated_at`-ul
   *  soluției, iar vizualizatorul își recitește documentul după el. */
  const laSchimbareRef = useRef(laSchimbare);
  laSchimbareRef.current = laSchimbare;
  const machetaRef = useRef(macheta);
  machetaRef.current = macheta;
  /** `adoptat`: macheta are deja pe ecran documentul scris (un instantaneu pus
   *  la loc), deci recitirea doar își notează revizia, nu-l mai aduce. */
  const recitesteSolutia = useCallback(async (adoptat = false) => {
    machetaRef.current?.urmeazaScriere();
    if (registrul) await registrul.reciteste();
    else window.dispatchEvent(new CustomEvent("optimus:solutii", { detail: { solutie: arhitecturaId } }));
    laSchimbareRef.current?.(adoptat);
  }, [registrul, arhitecturaId]);

  /** Starea `{stil, sectiune}` scrisă pe soluție, peste revizia `asteptata`:
   *  compunerea când se schimbă compoziția, altfel numai îmbrăcarea. */
  const scrieStarea = useCallback(async (tinta: { stil: StilFatada | null; sectiune: Record<string, unknown> },
                                         asteptata: number) => {
    if (!arhitecturaId) throw new Error(t("fatada.fara_arh"));
    machetaRef.current?.urmeazaScriere();
    const r = !aceeasiSectiune(tinta.sectiune, stareRef.current.sectiune)
      ? await api.compuneArhitectura(projectId, arhitecturaId, tinta.sectiune, undefined,
                                     { fatada: tinta.stil, revizie_asteptata: asteptata })
      : await api.imbracaSolutia(projectId, arhitecturaId, tinta.stil, asteptata);
    tineRevizia(r.revizie);
    return r.revizie;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, arhitecturaId, t]);

  /** SCRIEREA PE SOLUȚIE, cu un pas de anulare: stilul (și, când e dată,
   *  compoziția) se scriu în loc, numai peste revizia văzută. 409 = a scris
   *  altcineva între timp; nu se scrie nimic, iar macheta se recitește. */
  const scrie = useCallback(async (nou: { stil: StilFatada | null; sectiune?: Record<string, unknown> },
                                   ce: string) => {
    if (!arhitecturaId) throw new Error(t("fatada.fara_arh"));
    const inainte = stareRef.current;
    const docInainte = machetaRef.current?.instantaneu() ?? undefined;
    let revizie: number;
    try {
      revizie = await scrieStarea({ stil: nou.stil, sectiune: nou.sectiune ?? inainte.sectiune }, revizia());
    } catch (err) {
      if ((err as { status?: number }).status === 409) {
        golesteStiva(projectId, arhitecturaId); numara();
        await recitesteSolutia();
        throw new Error(t("fatada.calcat"));
      }
      throw err;
    }
    tinePas(projectId, arhitecturaId, { stil: inainte.stil, sectiune: inainte.sectiune,
                                        revizieDupa: revizie, ce, doc: docInainte });
    numara();
    await recitesteSolutia();
    return revizie;
  }, [projectId, arhitecturaId, recitesteSolutia, scrieStarea, revizia, numara, t]);

  /** ANULAREA ultimului pas: starea de dinainte scrisă înapoi pe soluție,
   *  numai dacă revizia e încă cea lăsată de pas. Cu instantaneu, macheta o
   *  arată PE LOC (fără reîncărcare, cu camera unde era) și scrierea vine în
   *  urmă; o scriere picată pune la loc ce era. Starea de după devine un pas de
   *  REFACERE — butonul din discuție se întoarce în «refă». */
  const anuleaza = useCallback(async (): Promise<{ ok: boolean; ce?: string; motiv?: string }> => {
    if (!arhitecturaId) return { ok: false, motiv: "fara_solutie" };
    const pas = ultimulPas(projectId, arhitecturaId);
    if (!pas) return { ok: false, motiv: "nimic_de_anulat" };
    if (fir.ruleaza) return { ok: false, motiv: "agentul_lucreaza" };
    const acum = revizia();
    let asteptata = pas.revizieDupa;
    if (asteptata < 0) {
      // pasul AGENTULUI, încă nefixat: dacă n-a scris nimic, n-ai ce anula
      if (acum === pas.revizieInainte) {
        scoatePas(projectId, arhitecturaId); numara();
        return { ok: false, motiv: "nimic_de_anulat" };
      }
      asteptata = acum;
    }
    if (asteptata !== acum) {
      golesteStiva(projectId, arhitecturaId); numara();
      return { ok: false, motiv: "calcat" };
    }
    const dupa = { stil: stareRef.current.stil, sectiune: stareRef.current.sectiune };
    const docDupa = machetaRef.current?.instantaneu() ?? undefined;
    const peLoc = pas.doc != null && docDupa != null;
    setLucrez("anulez"); setErori([]);
    if (peLoc) machetaRef.current!.restabileste(pas.doc);
    try {
      const r = await scrieStarea({ stil: pas.stil, sectiune: pas.sectiune }, asteptata);
      scoatePas(projectId, arhitecturaId);
      tineRefacere(projectId, arhitecturaId, { ...dupa, revizieDupa: r, ce: pas.ce, doc: docDupa });
      numara();
      setMesaj(t("fatada.anulat"));
      await recitesteSolutia(peLoc);
      return { ok: true, ce: pas.ce };
    } catch (err) {
      if (peLoc) machetaRef.current!.restabileste(docDupa);
      if ((err as { status?: number }).status === 409) {
        golesteStiva(projectId, arhitecturaId); numara();
        await recitesteSolutia();
        return { ok: false, motiv: "calcat" };
      }
      setErori(eroriDin(err));
      return { ok: false, motiv: (err as Error).message ?? String(err) };
    } finally {
      setLucrez("");
    }
  }, [projectId, arhitecturaId, recitesteSolutia, scrieStarea, revizia, numara, fir.ruleaza, t]);

  /** REFACEREA: starea de după schimbarea anulată, scrisă înapoi — în oglindă
   *  cu anularea; pasul ei de anulat se pune la loc, cu instantaneul de acum. */
  const reface = useCallback(async (): Promise<{ ok: boolean; ce?: string; motiv?: string }> => {
    if (!arhitecturaId) return { ok: false, motiv: "fara_solutie" };
    const pas = ultimaRefacere(projectId, arhitecturaId);
    if (!pas) return { ok: false, motiv: "nimic_de_refacut" };
    if (fir.ruleaza) return { ok: false, motiv: "agentul_lucreaza" };
    if (pas.revizieDupa !== revizia()) {
      golesteStiva(projectId, arhitecturaId); numara();
      return { ok: false, motiv: "calcat" };
    }
    const inainte = { stil: stareRef.current.stil, sectiune: stareRef.current.sectiune };
    const docInainte = machetaRef.current?.instantaneu() ?? undefined;
    const peLoc = pas.doc != null && docInainte != null;
    setLucrez("refac"); setErori([]);
    if (peLoc) machetaRef.current!.restabileste(pas.doc);
    try {
      const r = await scrieStarea({ stil: pas.stil, sectiune: pas.sectiune }, pas.revizieDupa);
      scoateRefacere(projectId, arhitecturaId);
      punePasInapoi(projectId, arhitecturaId, { ...inainte, revizieDupa: r, ce: pas.ce, doc: docInainte });
      numara();
      setMesaj(t("fatada.refacut"));
      await recitesteSolutia(peLoc);
      return { ok: true, ce: pas.ce };
    } catch (err) {
      if (peLoc) machetaRef.current!.restabileste(docInainte);
      if ((err as { status?: number }).status === 409) {
        golesteStiva(projectId, arhitecturaId); numara();
        await recitesteSolutia();
        return { ok: false, motiv: "calcat" };
      }
      setErori(eroriDin(err));
      return { ok: false, motiv: (err as Error).message ?? String(err) };
    } finally {
      setLucrez("");
    }
  }, [projectId, arhitecturaId, recitesteSolutia, scrieStarea, revizia, numara, fir.ruleaza, t]);

  const salveaza = useCallback(async () => {
    setLucrez("save"); setErori([]);
    try {
      await scrie({ stil }, t("fatada.pas_panou"));
      setSalvat(stil);
      setMesaj(t("fatada.salvat"));
    } catch (err) {
      setErori(eroriDin(err));
    } finally {
      setLucrez("");
    }
  }, [scrie, stil, t]);

  /** O ARHITECTURĂ NOUĂ din planul de situație: ia stilul proiectului, ca
   *  orice generare; regulile de aici rămân ale soluției deschise. */
  const regenereaza = useCallback(async () => {
    if (!planSituatieId) return;
    setLucrez("job");
    try {
      await api.startJob(projectId, "architecture", { upstream_solution_id: planSituatieId });
      setMesaj(t("fatada.job_pornit"));
      laJob?.();
    } catch (err) {
      setErori([(err as Error).message ?? String(err)]);
    } finally {
      setLucrez("");
    }
  }, [projectId, planSituatieId, laJob, t]);

  // AGENTUL PORNIT DIN DISCUȚIE: discuția se blochează din clipa pornirii, nu
  // abia când registrul îl vede rulând (câteva secunde mai târziu)
  const [agentPornit, setAgentPornit] = useState(false);
  const vazutRuland = useRef(false);
  useEffect(() => {
    if (fir.ruleaza) { vazutRuland.current = true; setAgentPornit(false); return; }
    if (!agentPornit) return;
    const ceas = setTimeout(() => setAgentPornit(false), 90_000);
    return () => clearTimeout(ceas);
  }, [fir.ruleaza, agentPornit]);
  const agentLucreaza = fir.ruleaza || agentPornit;

  // CE A SCRIS AGENTUL apare în machetă pe loc, nu la reîncărcarea paginii:
  // după fiecare pas cu scriere (stil pus, compunere) și la sfârșitul rulării.
  // Numai rândurile NOI ale unei rulări văzute rulând — firul unei rulări vechi,
  // citit la deschidere, n-are ce reciti.
  const scrieriVazute = useRef<number | null>(null);
  const scrieri = fir.randuri.filter((r) => r.cod && PASI_CARE_SCRIU.has(r.cod)).length;
  useEffect(() => {
    if (!fir.ruleaza && !vazutRuland.current) { scrieriVazute.current = scrieri; return; }
    if (scrieriVazute.current !== null && scrieri > scrieriVazute.current) laSchimbareRef.current?.();
    scrieriVazute.current = scrieri;
  }, [scrieri, fir.ruleaza]);
  const rulaInainte = useRef(false);
  useEffect(() => {
    if (rulaInainte.current && !fir.ruleaza) {
      void (async () => {
        if (registrul) await registrul.reciteste();
        laSchimbareRef.current?.();
      })();
    }
    rulaInainte.current = fir.ruleaza;
  }, [fir.ruleaza, registrul]);

  // PASUL AGENTULUI în stiva de anulare: revizia lui se află abia după ce a
  // terminat și macheta a recitit documentul scris de el
  useEffect(() => {
    if (!arhitecturaId || fir.ruleaza) return;
    const pas = ultimulPas(projectId, arhitecturaId);
    if (pas && pas.revizieDupa < 0 && stare.revizie !== pas.revizieInainte) {
      pas.revizieDupa = stare.revizie;
      tineRevizia(stare.revizie);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, arhitecturaId, fir.ruleaza, stare.revizie]);

  /** Ultima poză atașată în discuție, pentru agent (`preda_agentului`). */
  const ultimaPoza = useRef<string | null>(null);

  // FIȘELE SELECȚIEI: ce e fiecare element ales, ce regulă l-a pus, unde mai
  // apare; ce cuprinde fiecare zonă (v. `selectie.ts`). Cheia (`e:<id>`,
  // `z:<indice>`) leagă cipul de ce scoate și de ce aprinde pe machetă.
  const fiseCuCheie = useMemo(() => [
    ...(selectie?.elemente ?? []).map((e) => ({ cheie: `e:${e}`, f: fisaElement(doc, e) as Fisa | null })),
    ...(selectie?.zone ?? []).map((z, i) => ({ cheie: `z:${i}`, f: fisaZona(doc, z) as Fisa | null })),
  ].filter((x): x is { cheie: string; f: Fisa } => x.f !== null), [doc, selectie]);
  const fise = useMemo(() => fiseCuCheie.map((x) => x.f), [fiseCuCheie]);
  const fiseRef = useRef(fise);
  fiseRef.current = fise;

  const predaAgentului = useCallback(async (cerere: string, cuPoza: boolean, cuSelectia = true,
                                           refacere = false) => {
    if (!arhitecturaId) return { ok: false, motiv: "fara_solutie" };
    const inainte = stareRef.current;
    const reviziaInainte = revizia();
    const docInainte = machetaRef.current?.instantaneu() ?? undefined;
    try {
      await api.startJob(projectId, "facade", {
        upstream_solution_id: arhitecturaId, cerere,
        ...(cuPoza && ultimaPoza.current ? { poza: ultimaPoza.current } : {}),
        // REFACEREA: fațada se derivă de la zero după poză și cerere (v. `fatada/agent.py`)
        ...(refacere ? { refacere: true } : {}),
        // ce a ales omul pe machetă, ca fișe (fără geometrie)
        ...(cuSelectia && fiseRef.current.length ? { tinta: fiseRef.current } : {}),
      });
    } catch (err) {
      return { ok: false, motiv: (err as Error).message ?? String(err) };
    }
    tinePas(projectId, arhitecturaId, { stil: inainte.stil, sectiune: inainte.sectiune,
                                        revizieDupa: -1, revizieInainte: reviziaInainte,
                                        ce: cerere, doc: docInainte });
    numara();
    setAgentPornit(true);
    await recitesteSolutia();
    return { ok: true };
  }, [projectId, arhitecturaId, recitesteSolutia, revizia, numara]);

  // CONTEXTUL ASISTENTULUI, ținut stabil: uneltele se leagă o dată.
  const ref = useRef({ doc, scrie, anuleaza, reface, predaAgentului, agentLucreaza, arhitecturaId });
  ref.current = { doc, scrie, anuleaza, reface, predaAgentului, agentLucreaza, arhitecturaId };
  // …și LEGAT DE SOLUȚIA LUI (`legatDeSolutie`): după ce ecranul trece pe altă
  // arhitectură, uneltele unei discuții vechi refuză, nu citesc și nu scriu pe cea nouă
  const ctxAsistent = useMemo<ContextFatada>(() => legatDeSolutie({
    stare: () => ({ stil: stareRef.current.stil, sectiune: stareRef.current.sectiune,
                    pasi_de_anulat: arhitecturaId ? cativaPasi(projectId, arhitecturaId) : 0,
                    pasi_de_refacut: arhitecturaId ? cateRefaceri(projectId, arhitecturaId) : 0,
                    agent_lucreaza: ref.current.agentLucreaza,
                    selectie: fiseRef.current }),
    fete: () => feteleDin(ref.current.doc),
    construit: () => balustradele(ref.current.doc),
    etaje: () => {
      const niv = (ref.current.doc?.objects ?? []).filter((o) => o.type === "Storey"
        && (o.properties.level ?? 0) >= 0).map((o) => Number(o.properties.level ?? 0));
      return niv.length ? Math.max(...niv) + 1 : 0;
    },
    scrie: arhitecturaId ? (s, ce) => ref.current.scrie({ stil: s }, ce) : null,
    anuleaza: () => ref.current.anuleaza(),
    reface: () => ref.current.reface(),
    predaAgentului: arhitecturaId
      ? (cerere, cuPoza, cuSel, refacere) => ref.current.predaAgentului(cerere, cuPoza, cuSel, refacere) : null,
  }, arhitecturaId, () => ref.current.arhitecturaId), [projectId, arhitecturaId]);
  const unelte = useMemo(() => unelteFatada(ctxAsistent), [ctxAsistent]);

  /** BUTONUL DIN CÂMPUL DISCUȚIEI: anulează ultima schimbare a fațadei, apoi se
   *  întoarce în «refă» — omul se plimbă între fațada de dinainte și cea de
   *  după, iar discuția rămâne cum e (Cătălin, 13.10.2026). Fără pas de anulat
   *  sau de refăcut, nu apare. */
  const butonulDiscutiei = useMemo(() => {
    if (!potScrie || !arhitecturaId) return undefined;
    const fel = lucrez === "refac" || (lucrez !== "anulez" && refaceri > 0) ? "reface" as const
      : lucrez === "anulez" || pasi > 0 ? "anuleaza" as const : null;
    if (!fel) return undefined;
    return {
      fel,
      titlu: fel === "reface" ? t("fatada.reface_expl") : t("fatada.anuleaza_expl"),
      lucreaza: lucrez === "anulez" || lucrez === "refac",
      dezactivat: !!lucrez || agentLucreaza,
      apasa: () => {
        void (fel === "reface" ? reface() : anuleaza()).then((r) => {
          if (!r.ok && r.motiv === "calcat") setErori([t("fatada.calcat")]);
        });
      },
    };
  }, [potScrie, arhitecturaId, lucrez, refaceri, pasi, agentLucreaza, anuleaza, reface, t]);

  const aplicaJson = () => {
    try {
      const s = JSON.parse(text) as StilFatada;
      if (!Array.isArray(s.rules)) throw new Error("rules");
      setStil(s); setErori([]);
    } catch {
      setErori([t("fatada.json_stricat")]);
    }
  };

  const schimbaRegula = (i: number, r: ReguleFatada) => pune(reguli.map((x, k) => (k === i ? r : x)));
  const muta = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= reguli.length) return;
    const r = [...reguli]; [r[i], r[j]] = [r[j], r[i]]; pune(r);
  };

  return (
    <div className={`card panou-fatada${strans ? " strans" : ""}`}>
      <div className="cap-insorire">
        <b>{t("fatada.titlu")}</b>
        <span className="lbl">{reguli.length} {t("fatada.reguli")}{schimbat ? " ·" : ""}</span>
        <button className="ob-as-icon" onClick={() => setStrans(!strans)}
                aria-label={strans ? t("fatada.desfa") : t("fatada.strange")}>
          {strans ? "▸" : "▾"}
        </button>
      </div>
      {!strans && (
        <>
          {carte && <Inspiratie projectId={projectId} carte={carte} />}
          <div className="fatada-presets">
            <select value="" onChange={(e) => { const p = presets[e.target.value]; if (p) setStil(p); }}>
              <option value="">{t("fatada.porneste_de_la")}</option>
              {Object.keys(presets).map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <button className={json ? "on" : ""} onClick={() => setJson(!json)}>JSON</button>
          </div>

          {json ? (
            <div className="fatada-json">
              <textarea value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} />
              <button onClick={aplicaJson}>{t("fatada.aplica_json")}</button>
            </div>
          ) : (
            <ol className="fatada-reguli">
              {reguli.map((r, i) => {
                const fel = felRegulii(r);
                const sel = r.select ?? {};
                const rez = rezumat(r, tk);
                const deschisa = desfacuta === i;
                return (
                  <li key={i} className={`regula ${fel}${deschisa ? " deschisa" : ""}`}>
                    <div className="rezumat" onClick={() => setDesfacuta(deschisa ? null : i)}
                         role="button" tabIndex={0}
                         onKeyDown={(e) => { if (e.key === "Enter") setDesfacuta(deschisa ? null : i); }}>
                      <span className="chip-culoare" style={{ background: rez.culoare ?? "transparent" }} />
                      <span className="unde">{rez.unde}</span>
                      <span className="ce">{rez.ce}</span>
                      <span className="sp" />
                      <button onClick={(e) => { e.stopPropagation(); muta(i, -1); }} title={t("fatada.sus")}>↑</button>
                      <button onClick={(e) => { e.stopPropagation(); muta(i, 1); }} title={t("fatada.jos")}>↓</button>
                      <button onClick={(e) => { e.stopPropagation(); pune(reguli.filter((_, k) => k !== i)); if (desfacuta === i) setDesfacuta(null); }}
                              title={t("fatada.sterge")}>×</button>
                    </div>
                    {deschisa && (
                      <>
                        <div className="rand">
                          <select value={sel.surface ?? "facade"} title={t("fatada.suprafata")}
                                  onChange={(e) => schimbaRegula(i, { ...r, select: { ...sel, surface: e.target.value as never } })}>
                            {SUPRAFETE.map((sf) => <option key={sf} value={sf}>{tk(`fatada.surf.${sf}`)}</option>)}
                          </select>
                          <input value={etajeleCa(sel.storeys)} title={t("fatada.etaje")} size={6}
                                 onChange={(e) => schimbaRegula(i, { ...r, select: { ...sel, storeys: etajeleDin(e.target.value) } })} />
                          <input value={sel.region?.u ? `${sel.region.u[0]} → ${sel.region.u[1]}` : ""}
                                 placeholder={t("fatada.portiune")} title={t("fatada.portiune_ex")} size={14}
                                 onChange={(e) => {
                                   const v = e.target.value.trim();
                                   const m = v.split(/→|->|\.\./).map((x) => x.trim()).filter(Boolean);
                                   const region = m.length === 2 ? { ...(sel.region ?? {}), u: [m[0], m[1]] as [string, string] }
                                                : (() => { const { u: _u, ...rest } = sel.region ?? {}; return Object.keys(rest).length ? rest : undefined; })();
                                   schimbaRegula(i, { ...r, select: { ...sel, region } });
                                 }} />
                        </div>
                        <div className="rand">
                          {fel === "tile" && r.tile && (
                            <input type="color" value={r.tile.colour} title={t("fatada.placi_colorate")}
                                   onChange={(e) => schimbaRegula(i, { ...r, tile: { colour: e.target.value } })} />
                          )}
                          {fel === "finish" && r.finish && (
                            <>
                              <select value={r.finish.kind}
                                      onChange={(e) => { const kind = e.target.value as NonNullable<ReguleFatada["finish"]>["kind"];
                                        schimbaRegula(i, { ...r, finish: { ...r.finish!, kind, material: MATERIALE[kind]?.[0] ?? r.finish!.material } }); }}>
                                {FINISAJE.map((k) => <option key={k} value={k}>{tk(`kind.${k}`)}</option>)}
                              </select>
                              <input list={`mat-${i}`} value={r.finish.material} size={16} title={t("fatada.material")}
                                     onChange={(e) => schimbaRegula(i, { ...r, finish: { ...r.finish!, material: e.target.value } })} />
                              <datalist id={`mat-${i}`}>
                                {(MATERIALE[r.finish.kind] ?? []).map((m) => <option key={m} value={m} />)}
                              </datalist>
                              <input type="color" value={r.finish.colour ?? "#efe9dd"} title={t("fatada.culoare")}
                                     onChange={(e) => schimbaRegula(i, { ...r, finish: { ...r.finish!, colour: e.target.value } })} />
                            </>
                          )}
                          {fel === "band" && r.band && (
                            <>
                              <select value={String(r.band.at ?? "slab")} title={t("fatada.la")}
                                      onChange={(e) => schimbaRegula(i, { ...r, band: { ...r.band!, at: e.target.value } })}>
                                {["slab", "floor", "opening.top", "opening.bottom", "mid"].map((an) =>
                                  <option key={an} value={an}>{tk(`fatada.ancora.${an}`)}</option>)}
                              </select>
                              <label>h <input type="number" step={0.05} min={0.05} max={3} value={r.band.height_m}
                                              onChange={(e) => schimbaRegula(i, { ...r, band: { ...r.band!, height_m: Number(e.target.value) } })} /></label>
                              <label>d <input type="number" step={0.01} min={0.01} max={1} value={r.band.depth_m ?? 0.03}
                                              onChange={(e) => schimbaRegula(i, { ...r, band: { ...r.band!, depth_m: Number(e.target.value) } })} /></label>
                              <select value={typeof r.band.section === "string" ? r.band.section : "rect"} title={t("fatada.sectiune")}
                                      onChange={(e) => schimbaRegula(i, { ...r, band: { ...r.band!, section: e.target.value } })}>
                                {SECTIUNI.map((sc) => <option key={sc} value={sc}>{tk(`fatada.sectiune.${sc}`)}</option>)}
                              </select>
                              <input type="color" value={r.band.colour ?? "#f2efe6"} title={t("fatada.culoare")}
                                     onChange={(e) => schimbaRegula(i, { ...r, band: { ...r.band!, colour: e.target.value } })} />
                            </>
                          )}
                          {fel === "window" && r.window && (
                            <>
                              <input list={`tampl-${i}`} value={r.window.frame?.material ?? "pvc"} size={9} title={t("fatada.tamplarie")}
                                     onChange={(e) => schimbaRegula(i, { ...r, window: { ...r.window!, frame: { ...r.window!.frame, material: e.target.value } } })} />
                              <datalist id={`tampl-${i}`}>
                                {["pvc", "aluminiu", "lemn", "lemn_aluminiu"].map((m) => <option key={m} value={m} />)}
                              </datalist>
                              <label>{t("fatada.profil")} <input type="number" step={0.01} min={0.03} max={0.2} value={r.window.frame?.profile_m ?? 0.07}
                                              onChange={(e) => schimbaRegula(i, { ...r, window: { ...r.window!, frame: { ...r.window!.frame, profile_m: Number(e.target.value) } } })} /></label>
                              <select value={r.window.glazing ?? "double"} title={t("fatada.vitraj")}
                                      onChange={(e) => schimbaRegula(i, { ...r, window: { ...r.window!, glazing: e.target.value as never } })}>
                                {VITRAJE.map((k) => <option key={k} value={k}>{tk(`fatada.vitraj.${k}`)}</option>)}
                              </select>
                              <select value={r.window.opening ?? "tilt_turn"} title={t("fatada.deschidere")}
                                      onChange={(e) => schimbaRegula(i, { ...r, window: { ...r.window!, opening: e.target.value as never } })}>
                                {DESCHIDERI.map((k) => <option key={k} value={k}>{tk(`fatada.deschidere.${k}`)}</option>)}
                              </select>
                              <label>{t("fatada.canat")} ≤ <input type="number" step={0.1} min={0.4} max={3} value={r.window.leaf_max_m ?? 0.9}
                                              onChange={(e) => schimbaRegula(i, { ...r, window: { ...r.window!, leaf_max_m: Number(e.target.value), leaves: undefined } })} /></label>
                              <input type="color" value={r.window.colour ?? "#f4f4f2"} title={t("fatada.culoare")}
                                     onChange={(e) => schimbaRegula(i, { ...r, window: { ...r.window!, colour: e.target.value } })} />
                              <label title={t("fatada.glaf_expl")}><input type="checkbox" checked={r.window.sill !== null}
                                     onChange={(e) => schimbaRegula(i, { ...r, window: { ...r.window!, sill: e.target.checked ? { material: "glaf_aluminiu", depth_m: 0.04, height_m: 0.03, overhang_m: 0.03 } : null } })} /> {t("fatada.glaf")}</label>
                              <label title={t("fatada.spalet_expl")}><input type="checkbox" checked={r.window.reveal !== null}
                                     onChange={(e) => schimbaRegula(i, { ...r, window: { ...r.window!, reveal: e.target.checked ? { material: "etics", depth_m: 0.03 } : null } })} /> {t("fatada.spalet")}</label>
                            </>
                          )}
                          {fel === "floor" && r.floor && (
                            <>
                              <select value={r.floor.kind} title={t("fatada.pardoseala")}
                                      onChange={(e) => { const kind = e.target.value as NonNullable<ReguleFatada["floor"]>["kind"];
                                        // deck-ul stă numai pe terase: alegerea lui mută regula acolo
                                        const select = kind === "deck" ? { ...sel, surface: "terrace" as never } : sel;
                                        schimbaRegula(i, { ...r, select, floor: { kind, material: MATERIALE_PARDOSELII[kind][0],
                                                                                   colour: CULOARE_PARDOSELII[kind] } }); }}>
                                {PARDOSELI.map((k) => <option key={k} value={k}>{tk(`fatada.pardoseala.${k}`)}</option>)}
                              </select>
                              <input list={`pd-${i}`} value={r.floor.material ?? ""} size={16} title={t("fatada.material")}
                                     onChange={(e) => schimbaRegula(i, { ...r, floor: { ...r.floor!, material: e.target.value } })} />
                              <datalist id={`pd-${i}`}>
                                {(MATERIALE_PARDOSELII[r.floor.kind] ?? []).map((m) => <option key={m} value={m} />)}
                              </datalist>
                              <input type="color" value={r.floor.colour ?? CULOARE_PARDOSELII[r.floor.kind]} title={t("fatada.culoare")}
                                     onChange={(e) => schimbaRegula(i, { ...r, floor: { ...r.floor!, colour: e.target.value } })} />
                            </>
                          )}
                          {fel === "railing" && r.railing && (
                            <>
                              <select value={r.railing.kind}
                                      onChange={(e) => schimbaRegula(i, { ...r, railing: { ...r.railing!, kind: e.target.value as never } })}>
                                {BALUSTRADE.map((k) => <option key={k} value={k}>{tk(`kind.${k}`)}</option>)}
                              </select>
                              <label>h <input type="number" step={0.05} min={0.9} max={1.5} value={r.railing.height_m ?? 1.1}
                                              onChange={(e) => schimbaRegula(i, { ...r, railing: { ...r.railing!, height_m: Number(e.target.value) } })} /></label>
                              <input type="color" value={r.railing.colour ?? "#dbe8ec"} title={t("fatada.culoare")}
                                     onChange={(e) => schimbaRegula(i, { ...r, railing: { ...r.railing!, colour: e.target.value } })} />
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </li>
                );
              })}
            </ol>
          )}

          {!json && (
            <div className="fatada-adauga">
              <button onClick={() => pune([...reguli, regulaNoua("finish")])}>+ {t("fatada.finisaj")}</button>
              <button onClick={() => pune([...reguli, regulaNoua("band")])}>+ {t("fatada.brau")}</button>
              <button onClick={() => pune([...reguli, regulaNoua("railing")])}>+ {t("fatada.balustrada")}</button>
              <button onClick={() => pune([...reguli, regulaNoua("window")])}>+ {t("fatada.fereastra")}</button>
              <button onClick={() => pune([...reguli, regulaNoua("floor")])}>+ {t("fatada.pardoseala")}</button>
            </div>
          )}

          {erori.length > 0 && (
            <ul className="fatada-erori">{erori.map((e, i) => <li key={i}>{e}</li>)}</ul>
          )}
          {mesaj && !erori.length && <p className="note">{mesaj}</p>}
          {cantitati && (
            <details className="fatada-cantitati">
              <summary>{t("fatada.cantitati")}</summary>
              <table>
                <tbody>
                  {Object.entries(
                    Object.values(cantitati).reduce<Record<string, number>>((acc, c) => {
                      for (const [k, v] of Object.entries(c ?? {})) acc[k] = (acc[k] ?? 0) + v;
                      return acc;
                    }, {})).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                    <tr key={k}><td>{k}</td><td>{v.toFixed(1)}</td></tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}

          {placi.length > 0 && (
            <details className="fatada-cantitati fatada-placi">
              <summary>{t("fatada.placi_lista")}</summary>
              <table>
                <thead>
                  <tr><th>{t("fatada.placi_material")}</th><th>{t("fatada.placi_format")}</th>
                      <th>{t("fatada.placi_buc")}</th><th>m²</th></tr>
                </thead>
                <tbody>
                  {placi.flatMap((r) => [
                    <tr key={cheiePlaci(r)} className="placi-rand">
                      <td>
                        {r.colour && <span className="chip-culoare" style={{ background: r.colour }} />}
                        {numeMaterial(r.material)}{r.colour ? ` · ${r.colour.toUpperCase()}` : ""}
                      </td>
                      <td>{nf2.format(r.module_m[0])} × {nf2.format(r.module_m[1])} m</td>
                      <td>{nf0.format(r.count)}</td>
                      <td>{nf1.format(r.area_m2)}</td>
                    </tr>,
                    ...(placiDeschise.has(cheiePlaci(r)) ? r.sizes : r.sizes.slice(0, DIM_VIZIBILE)).map((d, i) => (
                      <tr key={`${r.material}|${r.colour}|${r.module_m.join("x")}|${d.width_m}x${d.height_m}`} className="placi-dim">
                        <td>{i === 0 ? t("fatada.placi_din_care") : ""}</td>
                        <td>{nf2.format(d.width_m)} × {nf2.format(d.height_m)} m</td>
                        <td>{nf0.format(d.count)}</td>
                        <td>{nf1.format(d.width_m * d.height_m * d.count)}</td>
                      </tr>
                    )),
                    ...(r.sizes.length > DIM_VIZIBILE ? [
                      <tr key={`${cheiePlaci(r)}|mai`} className="placi-dim">
                        <td colSpan={4}>
                          <button className="link" onClick={() => setPlaciDeschise((v) => {
                            const n = new Set(v);
                            if (n.has(cheiePlaci(r))) n.delete(cheiePlaci(r)); else n.add(cheiePlaci(r));
                            return n;
                          })}>
                            {placiDeschise.has(cheiePlaci(r)) ? t("fatada.placi_mai_putine")
                              : t("fatada.placi_inca", { n: r.sizes.length - DIM_VIZIBILE,
                                                          buc: nf0.format(r.sizes.slice(DIM_VIZIBILE).reduce((a, d) => a + d.count, 0)) })}
                          </button>
                        </td>
                      </tr>] : []),
                  ])}
                </tbody>
              </table>
            </details>
          )}

          <div className="fatada-actiuni">
            <button className="primar" disabled={!!lucrez || !arhitecturaId}
                    onClick={() => previzualizeaza(stil)}>
              {lucrez === "preview" ? t("fatada.se_previzualizeaza") : t("fatada.previzualizeaza")}
            </button>
            <button disabled={!!lucrez || !potScrie || !schimbat || !arhitecturaId || agentLucreaza}
                    onClick={salveaza} title={t("fatada.salveaza_expl")}>
              {lucrez === "save" ? t("fatada.se_salveaza") : t("fatada.salveaza")}
            </button>
            {pasi > 0 && (
              <button disabled={!!lucrez || !potScrie || agentLucreaza}
                      onClick={() => { void anuleaza().then((r) => {
                        if (!r.ok && r.motiv === "calcat") setErori([t("fatada.calcat")]); }); }}
                      title={t("fatada.anuleaza_expl")}>
                {lucrez === "anulez" ? t("fatada.se_anuleaza") : t("fatada.anuleaza")}
              </button>
            )}
            {refaceri > 0 && (
              <button disabled={!!lucrez || !potScrie || agentLucreaza}
                      onClick={() => { void reface().then((r) => {
                        if (!r.ok && r.motiv === "calcat") setErori([t("fatada.calcat")]); }); }}
                      title={t("fatada.reface_expl")}>
                {lucrez === "refac" ? t("fatada.se_reface") : t("fatada.reface")}
              </button>
            )}
            <button disabled={!!lucrez || !potScrie || !planSituatieId} onClick={regenereaza}
                    title={t("fatada.regenereaza_expl")}>
              {lucrez === "job" ? t("fatada.se_porneste") : t("fatada.regenereaza")}
            </button>
          </div>

        </>
      )}
      {/* ASISTENTUL stă în coloana vizualizatorului, sub panoul ăsta, iar
          butonul lui lângă 2D/3D (`locAsistent`); când se deschide, panoul
          ăsta se strânge, ca discuția să aibă înălțimea coloanei. */}
      <Asistent projectId={projectId} cod={arhitecturaId ?? planSituatieId ?? "fatada"}
                potScrie={potScrie} unde="fatada" unelte={unelte}
                titlu={t("fatada.asistent")} subtitlu={solutiaNumita} loc={locAsistent} fir={fir}
                prietenos pasi={PASII_FATADEI}
                blocat={agentLucreaza ? t("agent.discutie_blocata")
                        : lucrez === "anulez" || lucrez === "refac" ? t("fatada.discutie_asteapta") : undefined}
                actiune={butonulDiscutiei}
                laPoza={(p) => { ultimaPoza.current = p; }}
                stare={() => ({ selectie: fiseRef.current })}
                cipuri={fiseCuCheie.map(({ cheie, f }) => ({
                  text: eticheta(f), titlu: f.ce,
                  aprinde: laAprinde ? (da: boolean) => laAprinde(da ? cheie : null) : undefined,
                  scoate: () => {
                    if (!selectie || !laSelectie) return;
                    laAprinde?.(null);
                    laSelectie(cheie.startsWith("e:")
                      ? { ...selectie, elemente: selectie.elemente.filter((e) => `e:${e}` !== cheie) }
                      : { ...selectie, zone: selectie.zone.filter((_, k) => `z:${k}` !== cheie) });
                  } }))}
                golesteCipuri={laSelectie ? () => laSelectie({ elemente: [], zone: [] }) : undefined}
                laDeschidere={(d) => { setAsistentDeschis(d); if (d) setStrans(true); }} />
    </div>
  );
}
